import classNames from 'classnames';
import Immutable from 'immutable';
import React, { ComponentType } from 'react';
import type { ConnectDragSource, DragSourceConnector, DragSourceMonitor, DragSourceSpec } from 'react-dnd';
import { DndComponentClass, DragSource } from 'react-dnd';

import TableCell from 'sharedreact/components/TableCell';
import TableRowMoveTarget from 'sharedreact/components/TableRowMoveTarget';
import type TableColumn from 'sharedreact/data/TableColumn';

interface RowType {
  id?: string // TODO should not be optional, but some data records still don't have an id
}

type DragObjectType<T> = {
  item: T,
  rowIndex: number
};

type DragSourceComponentProps = {
  connectDragSource: ConnectDragSource
};

type Props<T extends RowType> = DragSourceComponentProps & {
  row: T,
  rowIndex: number,
  selected: boolean,
  disabled: boolean,
  showSelection?: boolean,
  tableColumns: Immutable.List<TableColumn<T>>,
  columnWidths: Immutable.List<string>,
  rowHeight?: number,
  reorderColumnActive: boolean,
  reorderColumn: number,
  highlight: boolean,

  onClick: (rowId: string, event?: React.MouseEvent<HTMLTableRowElement>) => void,
  onHover: (sourceItem: any, targetItem: T, position: string) => void,
  onDrop: (sourceItem: any, targetItem: T, position: string) => void,
  clearHighlight?: (rowId: string) => void,
  getClassNameOnDragHover?: (sourceItem: any, targetItem: T, position: string) => string,
  itemCanDragDrop?: (item: T) => boolean,
  itemGetDragDropItemType?: (item :T) => string,
  itemAcceptsDropItemTypes?: (item: T) => string[],
};

class TableRowComponent<T extends RowType> extends React.PureComponent<Props<T>> {
  override componentDidMount (): void {
    const {highlight, clearHighlight, row} = this.props;

    if (highlight && clearHighlight != null && row.id != null) {
      setTimeout(() => clearHighlight(row.id!), 2000);
    }
  }

  override componentDidUpdate (prevProps: Readonly<Props<T>>): void {
    const {highlight, clearHighlight, row} = this.props;

    if (highlight && clearHighlight != null && !prevProps.highlight && row.id != null) {
      setTimeout(() => clearHighlight(row.id!), 2000);
    }
  }

  override render (): React.ReactNode {
    const { row, rowIndex, onClick, tableColumns,
      columnWidths, selected, showSelection, disabled, rowHeight, reorderColumnActive,
      itemCanDragDrop, itemGetDragDropItemType, // eslint-disable-line no-unused-vars
      reorderColumn, highlight,
      getClassNameOnDragHover,
      onHover, onDrop, itemAcceptsDropItemTypes, connectDragSource } = this.props;

    let dataComponents: any = null;
    let rowClassName: string | undefined = undefined;
    let handleOnClick: React.MouseEventHandler<HTMLTableRowElement> | undefined = undefined;
    if (row != null) {
      dataComponents = tableColumns.map((tableColumn, columnIndex) => {
        const content = tableColumn.renderCell != null ? tableColumn.renderCell(row, rowIndex, columnIndex) : undefined;
        const tooltip = (tableColumn.renderToolTip != null) ? tableColumn.renderToolTip(row, rowIndex, columnIndex) : undefined;
        const alignCenter: boolean = (tableColumn.alignCenter != null && tableColumn.alignCenter === true);
        const alignRight: boolean = (tableColumn.alignRight != null && tableColumn.alignRight === true);
        const columnWidth: string = columnWidths.get(columnIndex) || '';
        const isReorderingColumn: boolean = reorderColumnActive && reorderColumn >= 0 && reorderColumn === columnIndex;
        const cellClassName: string | undefined = tableColumn.cellClassName;

        return (
          <TableCell key={'drowcol' + rowIndex + '-' + columnIndex}
            className={cellClassName}
            content={content}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            columnWidth={columnWidth}
            alignCenter={alignCenter}
            alignRight={alignRight}
            isReorderingColumn={isReorderingColumn}
            rowHeight={rowHeight}
            tooltip={tooltip}
          />
        );
      });

      rowClassName = 'esko_tablerow';
      if (row.id != null) {
        rowClassName = classNames(
          rowClassName,
          { 'esko_selected': selected,
            'esko_highlight': highlight,
            'esko_disabled': disabled }
        );
      }
      handleOnClick = (event?: React.MouseEvent<HTMLTableRowElement>) => row.id != null ? onClick(row.id, event) : undefined;
    } else {
      dataComponents = tableColumns.map((tableColumn, columnIndex) => {
        const alignCenter: boolean = (tableColumn.alignCenter != null && tableColumn.alignCenter === true);
        const alignRight: boolean = (tableColumn.alignRight != null && tableColumn.alignRight === true);
        const columnWidth: string = columnWidths.get(columnIndex) || '';
        const isReorderingColumn: boolean = reorderColumnActive && reorderColumn >= 0 && reorderColumn === columnIndex;

        return (
          <TableCell key={'drowcol' + rowIndex + '-' + columnIndex}
            content=' '
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            columnWidth={columnWidth}
            alignCenter={alignCenter}
            alignRight={alignRight}
            isReorderingColumn={isReorderingColumn}
            rowHeight={rowHeight}
          />
        );
      });
    }

    if (showSelection === true || getClassNameOnDragHover != null || itemAcceptsDropItemTypes != null) {
      return (
        <TableRowMoveTarget<T>
          key={row != null && row.id != null ? 'drowmovetarget_id_' + row.id : 'drowmovetarget_id_' + rowIndex}
          item={row}
          className={rowClassName}
          onClick={handleOnClick}
          onHover={onHover}
          onDrop={onDrop}
          getClassNameOnDragHover={getClassNameOnDragHover}
          itemAcceptsDropItemTypes={itemAcceptsDropItemTypes}
          ref={instance => connectDragSource(instance)}
        >
          {dataComponents}
        </TableRowMoveTarget>
      );
    } else {
      return (
        <tr className={rowClassName} onClick={handleOnClick}>{dataComponents}</tr>
      );
    }
  }
}

function sourceItemType<T extends RowType>(props: Props<T>) {
  return props.itemGetDragDropItemType ? props.itemGetDragDropItemType(props.row) : 'ITEM_NO_DND_SUPPORT';
};

const source: DragSourceSpec<Props<any>, DragObjectType<any>> = {
  beginDrag<T extends RowType> (props: Props<T>): DragObjectType<T> {
    return {item: props.row, rowIndex: props.rowIndex};
  },
  canDrag<T extends RowType> (props: Props<T>): boolean {
    return props.itemCanDragDrop ? props.itemCanDragDrop(props.row) : false;
  }
};

function collect (connect: DragSourceConnector, monitor: DragSourceMonitor): DragSourceComponentProps {
  return {
    connectDragSource: connect.dragSource()
  };
}

class TableRow<T extends RowType> extends React.PureComponent<Omit<Props<T>, keyof DragSourceComponentProps>> {
  private _tableRowDragSource:  React.ComponentClass<Omit<Props<T>, keyof DragSourceComponentProps>>;

  constructor (props: Omit<Props<T>, keyof DragSourceComponentProps>) {
    super(props);

    this._tableRowDragSource = DragSource(
      sourceItemType,
      source,
      collect
    )(TableRowComponent<T>);
  }

  override render (): React.ReactNode {
    const TableRowDragSource = this._tableRowDragSource;

    return (
      <TableRowDragSource {...this.props} />
    );
  }
}

export { RowType };

export default TableRow;
